import { AdminProfile } from '../contexts/AuthContext';
import { Event } from '../types/models/event';
import { User } from '../types/models/user';
import { INDIA_STATES } from '../constants/indiaStates';

export const ALL_INDIA_REGION = 'All India';

const normalizeRegionValue = (value?: string | null): string => {
  return (value || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const ALL_INDIA_REGION_NORMALIZED = normalizeRegionValue(ALL_INDIA_REGION);
const ALL_INDIA_REGION_ALIASES = new Set([
  ALL_INDIA_REGION_NORMALIZED,
  normalizeRegionValue('India'),
  normalizeRegionValue('All Region'),
  normalizeRegionValue('All Regions'),
  normalizeRegionValue('Pan India'),
  normalizeRegionValue('National'),
  normalizeRegionValue('Nationwide')
]);

const CANONICAL_INDIA_REGIONS = INDIA_STATES.map((state) => state.name);
const CANONICAL_INDIA_REGION_SET = new Set(
  CANONICAL_INDIA_REGIONS.map((region) => normalizeRegionValue(region))
);

const REGION_ALIAS_TO_CANONICAL = new Map<string, string>(
  [
    ...INDIA_STATES.flatMap((state) => {
      const canonical = normalizeRegionValue(state.name);
      const codeAlias = normalizeRegionValue(state.code);
      return [
        [canonical, canonical] as const,
        [codeAlias, canonical] as const
      ] as const;
    }),
    ...Array.from(ALL_INDIA_REGION_ALIASES).map((alias) => [alias, ALL_INDIA_REGION_NORMALIZED] as const)
  ]
);

const canonicalizeRegionValue = (value?: string | null): string => {
  const normalized = normalizeRegionValue(value);
  return REGION_ALIAS_TO_CANONICAL.get(normalized) || normalized;
};

export const isAllIndiaRegionValue = (value?: string | null): boolean => {
  return canonicalizeRegionValue(value) === ALL_INDIA_REGION_NORMALIZED;
};

const canonicalizeAssignedRegionSet = (regions: string[] = []): Set<string> => {
  return new Set(
    regions
      .map((region) => canonicalizeRegionValue(region))
      .filter(Boolean)
  );
};

export const areAllRegionsAssigned = (regions: string[] = []): boolean => {
  const canonicalRegions = canonicalizeAssignedRegionSet(regions);

  if (canonicalRegions.has(ALL_INDIA_REGION_NORMALIZED)) {
    return true;
  }

  return Array.from(CANONICAL_INDIA_REGION_SET).every((region) => canonicalRegions.has(region));
};

export const expandAssignedRegions = (regions: string[] = []): string[] => {
  if (areAllRegionsAssigned(regions)) {
    return [...CANONICAL_INDIA_REGIONS];
  }

  return regions
    .map((region) => String(region || '').trim())
    .filter(Boolean);
};

export const normalizeAssignedRegions = (regions: string[] = []): string[] => {
  const cleanedRegions = regions
    .map((region) => String(region || '').trim())
    .filter(Boolean);

  if (areAllRegionsAssigned(cleanedRegions)) {
    return [ALL_INDIA_REGION];
  }

  return Array.from(new Set(cleanedRegions));
};

export const toggleAssignedRegion = (regions: string[] = [], region: string): string[] => {
  const normalizedRegion = String(region || '').trim();

  if (!normalizedRegion) {
    return normalizeAssignedRegions(regions);
  }

  if (isAllIndiaRegionValue(normalizedRegion)) {
    return areAllRegionsAssigned(regions) ? [] : [ALL_INDIA_REGION];
  }

  const expandedRegions = expandAssignedRegions(regions);
  const hasRegion = expandedRegions.includes(normalizedRegion);
  const nextRegions = hasRegion
    ? expandedRegions.filter((entry) => entry !== normalizedRegion)
    : [...expandedRegions, normalizedRegion];

  return normalizeAssignedRegions(nextRegions);
};

const assignedRegionsMatchAllIndiaScope = (regions: string[] = []): boolean => {
  return areAllRegionsAssigned(regions);
};

const candidateIsAllIndiaScope = (value?: string | null): boolean => {
  return isAllIndiaRegionValue(value);
};


export const isRegionScopedAdmin = (profile?: AdminProfile | null): boolean => {
  return Boolean(profile && (profile.role === 'admin' || profile.role === 'employee'));
};

export const getAssignedRegions = (profile?: AdminProfile | null): string[] => {
  if (!isRegionScopedAdmin(profile)) {
    return [];
  }

  return normalizeAssignedRegions(Array.isArray(profile?.regions) ? profile!.regions : []);
};

export const matchesAssignedRegions = (
  profile: AdminProfile | null | undefined,
  ...values: Array<string | null | undefined>
): boolean => {
  if (!isRegionScopedAdmin(profile)) {
    return true;
  }

  const assignedRegions = getAssignedRegions(profile);
  if (assignedRegions.length === 0) {
    return false;
  }

  const normalizedCandidates = values
    .map(normalizeRegionValue)
    .filter(Boolean);

  if (normalizedCandidates.length === 0) {
    return false;
  }

  if (assignedRegionsMatchAllIndiaScope(assignedRegions)) {
    return true;
  }

  if (normalizedCandidates.some((candidate) => candidateIsAllIndiaScope(candidate))) {
    return false;
  }

  return assignedRegions.some((region) => {
    const normalizedRegion = normalizeRegionValue(region);
    const canonicalRegion = canonicalizeRegionValue(region);

    return normalizedCandidates.some((candidate) =>
      candidate.includes(normalizedRegion) ||
      normalizedRegion.includes(candidate) ||
      candidate.includes(canonicalRegion) ||
      canonicalRegion.includes(candidate)
    );
  });
};

export const userMatchesAssignedRegions = (
  profile: AdminProfile | null | undefined,
  user: User
): boolean => {
  return matchesAssignedRegions(profile, user.state, user.location, user.city);
};

export const eventMatchesAssignedRegions = (
  profile: AdminProfile | null | undefined,
  event: Event
): boolean => {
  return matchesAssignedRegions(profile, event.location);
};
